import React, {
  FC,
  MouseEvent,
  ReactNode,
  useCallback,
  useRef,
} from 'react'
import { Box, IconButton } from '@pogokid/mui'
import { useEffectOnce } from '@pogokid/react-hooks'
import CloseIcon from '@mui/icons-material/Close'
import { Toolbar } from '@mui/material'
import cx from 'classnames'
import styles from './ActionSection.module.css'
import { Text } from '~m/designSystem'

export interface ActionSectionButton {
  id: string
  title: string
  icon: ReactNode
}

interface ActionSectionProps {
  title: string
  defaultExpanded?: boolean
  className?: string
  buttons?: ActionSectionButton[]
  /**
   * Show attention animation to help
   * users see this section has appeared
   */
  withAttention?: boolean

  onButtonClick?(buttonId: string): void

  onClose?(): void
}

export const ActionSection: FC<
  React.PropsWithChildren<ActionSectionProps>
> = ({
  title,
  defaultExpanded: _defaultExpanded,
  className,
  buttons,
  withAttention,
  onButtonClick,
  onClose,
  children,
}) => {
  const elRef = useRef<HTMLDivElement>(null)

  useEffectOnce(() => {
    if (withAttention) {
      elRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
  })

  const onButtonsClick = useCallback(
    (id: string, ev: MouseEvent<HTMLButtonElement>) => {
      if (onButtonClick) {
        onButtonClick(id)
      }
      ev.stopPropagation()
    },
    [onButtonClick]
  )
  return (
    <div
      ref={elRef}
      className={cx(styles.ActionSection, className, {
        [styles.ActionSection__withAttention]: withAttention,
      })}
    >
      <Toolbar
        className={styles.ActionSection__toolbar}
        disableGutters
        variant="dense"
      >
        <Text component={'h2'} variant={'section-header'}>
          {title}
        </Text>
        <Box flexGrow={1} />
        {buttons && buttons.length > 0 && (
          <>
            {buttons.map(({ id, title, icon }) => (
              <IconButton
                key={id}
                title={title}
                data-button-id={id}
                size={'small'}
                onClick={(ev) => onButtonsClick(id, ev)}
              >
                {icon}
              </IconButton>
            ))}
          </>
        )}
        {onClose && (
          <IconButton size={'small'} onClick={onClose}>
            <CloseIcon fontSize={'inherit'} />
          </IconButton>
        )}
      </Toolbar>
      <Box className={styles.ActionSection__content}>
        {children}
      </Box>
    </div>
  )
}
